import { motion } from "framer-motion";
import {
  ArrowRight,
  Braces,
  Check,
  FileText,
  Plug,
  Search,
  Terminal,
} from "lucide-react";
import { Wordmark } from "./Wordmark";

const STEPS = [
  {
    n: "01",
    title: "Upload your files.",
    body: "Bring the knowledge you already have.",
    art: (
      <div className="relative flex h-36 items-center justify-center">
        <div className="absolute h-24 w-20 -translate-x-5 -rotate-6 rounded-lg border border-[#233c4d]/60 bg-[#10191f]" />
        <div className="absolute h-24 w-20 translate-x-5 rotate-6 rounded-lg border border-[#233c4d]/60 bg-[#111813]" />
        <div className="relative flex h-24 w-20 flex-col items-center justify-center gap-1.5 rounded-lg border border-[#bddcec]/30 bg-[#14232d] text-[#bddcec] shadow-[0_12px_32px_rgba(0,0,0,0.4)]">
          <FileText className="h-5 w-5" aria-hidden="true" />
          <span className="text-[10px] font-medium text-[#edf2f4]">Your files</span>
          <span className="font-mono text-[9px] text-[#889296]">PDF · TXT · MD</span>
        </div>
      </div>
    ),
  },
  {
    n: "02",
    title: "Ask in your own words.",
    body: "No need to know the exact phrase.",
    art: (
      <div className="relative flex h-36 items-center justify-center">
        <div className="flex items-center gap-3 rounded-2xl border border-[#233c4d]/50 bg-[#10191f] px-5 py-4 shadow-[0_12px_32px_rgba(0,0,0,0.4)]">
          <Wordmark compact />
          <span className="font-mono text-xs text-[#889296]">wondersearch</span>
          <span className="h-2 w-2 animate-pulse rounded-full bg-[#ff8a4c]" />
        </div>
      </div>
    ),
  },
  {
    n: "03",
    title: "Find what you need.",
    body: "Relevant information, ready to use.",
    art: (
      <div className="relative flex h-36 items-center justify-center">
        <div className="w-52 rounded-2xl border border-[#233c4d]/50 bg-[#111813] p-4 shadow-[0_12px_32px_rgba(0,0,0,0.4)]">
          <div className="flex items-center gap-2 rounded-full border border-[#233c4d]/40 bg-[#0b100d] px-3 py-1.5 text-[10px] text-[#889296]">
            <Search className="h-3 w-3" aria-hidden="true" /> Your question
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs font-medium text-[#bce8d1]">
            <Check className="h-3.5 w-3.5" aria-hidden="true" /> The right information
          </div>
          <div className="mt-2 flex items-center gap-1.5 font-mono text-[9px] text-[#889296]">
            <FileText className="h-3 w-3" aria-hidden="true" /> From your files
          </div>
        </div>
      </div>
    ),
  },
];

const CHANNELS = [
  { label: "MCP", icon: Plug },
  { label: "API", icon: Braces },
  { label: "CLI", icon: Terminal },
  { label: "SDK", icon: Braces },
];

export const StepsFlow = () => (
  <section
    data-testid="steps-flow"
    className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32"
  >
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      <span className="eyebrow">Your files. Everywhere you work.</span>
      <h2 className="mt-4 text-3xl font-medium tracking-tight text-[#f3f5f3] md:text-4xl lg:text-5xl">
        Upload your files.
        <br />
        <span className="text-[#889296]">Start asking.</span>
      </h2>
      <p className="mt-4 text-sm text-[#889296] md:text-base">
        Instantly searchable. No embeddings required.
      </p>
    </motion.div>

    <ol className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
      {STEPS.map((step, i) => (
        <motion.li
          key={step.n}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
          data-testid={`step-card-${step.n}`}
          className="rounded-3xl border border-[#233c4d]/30 bg-[#111813] p-6 transition-colors duration-300 hover:border-[#233c4d]/70 md:p-8"
        >
          {step.art}
          <span className="font-mono text-xs tracking-[0.2em] text-[#ff8a4c]">
            {step.n}
          </span>
          <h3 className="mt-2 text-xl font-medium text-[#f3f5f3] md:text-2xl">
            {step.title}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-[#889296]">{step.body}</p>
        </motion.li>
      ))}
    </ol>

    <div className="mt-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
      <div className="flex flex-wrap items-center gap-3" data-testid="channel-chips">
        <span className="text-sm text-[#889296]">Search through</span>
        {CHANNELS.map(({ label, icon: Icon }) => (
          <span
            key={label}
            data-testid={`channel-${label.toLowerCase()}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#233c4d]/40 bg-[#10191f] px-3.5 py-1.5 font-mono text-xs text-[#c3cbce]"
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            {label}
          </span>
        ))}
      </div>
      <a
        href="https://app.wondersearch.ai/"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="steps-cta-link"
        className="group inline-flex items-center gap-2 text-sm font-semibold text-[#edf2f4] transition-colors duration-200 hover:text-[#bce8d1]"
      >
        Make your files searchable
        <ArrowRight
          className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
          aria-hidden="true"
        />
      </a>
    </div>
  </section>
);
